import { InputAdornment } from "@mui/material";
import { useAchievements } from "api/achievements";
import { useAppSelector } from "app/hooks";
import { NumberFormatInput } from "components/FormControls/NumberFormatInput";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { SelectInput } from "components/FormControls/Select/Select";
import { findConditionLabel } from "components/Layout/Campaign/Common/CampaignRules/Rule/Conditions/utils";
import { useMessage } from "hooks/intl";
import { useManageInfiniteScroll } from "hooks/useManageInfiniteScroll";
import { get } from "lodash";
import { useEffect, useState } from "react";
import { selectDefaultLanguageData } from "reduxState/translations/translationsSlice";
import { AchievementCompleteRulePeriod } from "types/achievements";
import { AchievementProgressionType } from "types/segments";
import { formatNumberValue } from "utilities/numbers";
import { pluralizeValueAdornment } from "utilities/strings";
import { BoxElement } from "./BoxElement";
import { useStyles } from "./SegmentConditions.styles";
import { segmentAchievementGoal, segmentConditions } from "./helpers";

export const initialValuesAchievementProgression: AchievementProgressionType = {
  achievementId: "",
  achievementRuleId: "",
  operator: { code: "" },
  progress: 0,
  fromProgress: 0,
  toProgress: 0,
  type: "achievement_rule_progress",
};

export function AchievementProgression({
  formField,
  values,
  status,
  readOnly,
  setFieldValue,
}: {
  formField: string;
  values: any;
  status: any;
  readOnly?: boolean;

  setFieldValue: (field: string, value: any) => void;
}) {
  const classes = useStyles();
  const value = get(values, formField);

  const { message } = useMessage();
  const defaultLang = useAppSelector(selectDefaultLanguageData);
  const [achievementSearch, setAchievementSearch] = useState<string>();
  const { useAchievementData } = useAchievements();
  const { data: selectedAchievementData } = useAchievementData(
    value?.achievementId as string,
  );
  const { useInfiniteAchievmentsList } = useAchievements();
  const {
    data: infiniteAchievementsList,
    size: achievementsSize,
    setSize: setAchievementsSize,
    isValidating,
  } = useInfiniteAchievmentsList({
    filters: achievementSearch
      ? [
          {
            condition: "like",
            query: "name",
            value: achievementSearch,
          },
        ]
      : [],
    order: "asc",
    sortKey: "name",
  });

  const {
    infiniteData: achievementsList,
    anyElementsToFetch: anyAchievementsToFetch,
  } = useManageInfiniteScroll(infiniteAchievementsList || []);

  const achievementsItems =
    achievementsList.map((achievement) => {
      return {
        id: achievement.achievementId,
        label: achievement.name,
      };
    }) || [];

  const selectedAchievementRulesItems = (
    selectedAchievementData?.rules || []
  ).map((rule, index) => {
    return {
      id: rule?.achievementRuleId as string,
      label:
        (rule?.translations && rule?.translations[defaultLang?.code]?.name) ||
        `RULE ${index + 1}`,
      goal: rule?.completeRule?.periodGoal,
      days: rule?.completeRule?.period?.value || undefined,
      periodType: rule?.completeRule?.period?.type,
      consecutive: rule?.completeRule?.period?.consecutive || undefined,
    };
  });

  const isProgressCountRange = value.operator.code === "is_number_between";
  const achievementIdError = get(status, "achievementId");
  const achievementRuleIdError = get(status, "achievementRuleId");
  const progressNumberError = get(status, "progress");
  const fromProgressNumberError = get(status, "fromProgress");
  const toProgressNumberError = get(status, "toProgress");

  const selectedRule = selectedAchievementRulesItems?.find(
    (rule) => rule.id === value.achievementRuleId,
  );

  const isCompletedPeriodsAchievementGoal =
    value?.achievementGoal === "completed_periods";

  const isSelectDisabled =
    selectedAchievementRulesItems?.length === 1 &&
    value.achievementRuleId === selectedAchievementRulesItems[0]?.id;

  const selectedAchievementName =
    selectedAchievementData?.translations[defaultLang?.code]?.name;

  const consecutiveRulePeriodTypes = ["day", "week", "month", "year"];

  const checkIfConsecutiveRule = (id?: string) => {
    const rulePeriodType = selectedAchievementRulesItems?.find(
      (rule) => rule.id === id,
    )?.periodType;

    if (rulePeriodType) {
      return consecutiveRulePeriodTypes.includes(rulePeriodType);
    }
  };

  const overallRuleAttrLabel = message(
    {
      id: "ofAttrValue",
    },
    { value: formatNumberValue(selectedRule?.goal) },
  ).toLocaleLowerCase();

  const lastXDaysRuleAttrLabel =
    selectedRule?.days &&
    message(
      {
        id: "ofEventAttrPerPeriod",
      },
      {
        value: formatNumberValue(selectedRule?.goal),
        period: `${formatNumberValue(selectedRule?.days)} ${message({ id: pluralizeValueAdornment("day", selectedRule?.days) })}`,
      },
    ).toLocaleLowerCase();

  const consecutiveRuleCurrentProgressAttrLabel = message(
    {
      id: "ofEventAttrPerPeriod",
    },
    {
      value: formatNumberValue(selectedRule?.goal),
      period: message({ id: selectedRule?.periodType }),
    },
  ).toLocaleLowerCase();

  const consecutiveRuleCompletedPeriodsAttrLabel =
    (selectedRule &&
      selectedRule?.consecutive &&
      `${message({
        id: "of",
      })} ${selectedRule?.consecutive} ${message({ id: pluralizeValueAdornment(selectedRule?.periodType, selectedRule?.consecutive) }).toLocaleLowerCase()}`) ||
    "";

  const getSelectedRuleAtrrLabel = () => {
    const { OVERALL, LAST_DAY, DAY, WEEK, MONTH, YEAR } =
      AchievementCompleteRulePeriod;

    switch (selectedRule?.periodType) {
      case OVERALL:
        return overallRuleAttrLabel;
      case LAST_DAY:
        return lastXDaysRuleAttrLabel;
      case DAY:
      case WEEK:
      case MONTH:
      case YEAR:
        if (isCompletedPeriodsAchievementGoal) {
          return consecutiveRuleCompletedPeriodsAttrLabel;
        } else {
          return consecutiveRuleCurrentProgressAttrLabel;
        }
      default:
        return "";
    }
  };

  const selectedRuleAttrLabel = getSelectedRuleAtrrLabel();

  useEffect(() => {
    if (
      value?.achievementRuleId === "" &&
      selectedAchievementRulesItems?.length === 1
    ) {
      setFieldValue(
        `${formField}.achievementRuleId`,
        selectedAchievementRulesItems[0]?.id,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAchievementData]);

  return (
    <>
      {value.isConditionSaved || readOnly ? (
        <>
          <BoxElement
            content={selectedAchievementName || value.achievementId}
            error={achievementIdError}
          />
          {message({ id: "whereIn" }).toLocaleLowerCase()}
          <BoxElement
            content={
              selectedAchievementRulesItems.find(
                (item) => item.id === value.achievementRuleId,
              )?.label || value.achievementRuleId
            }
            error={achievementRuleIdError}
          />{" "}
          {message({
            id: isCompletedPeriodsAchievementGoal
              ? "completedPeriods"
              : "currentProgress",
          }).toLocaleLowerCase()}{" "}
          {message({
            id: findConditionLabel(value.operator.code),
          }).toLocaleLowerCase()}
          {isProgressCountRange ? (
            <>
              {message({ id: "is_between" }).toLocaleLowerCase()}{" "}
              <BoxElement
                content={formatNumberValue(value?.fromProgress, "")}
                error={fromProgressNumberError}
              />{" "}
              {message({ id: "and" }).toLocaleLowerCase()}
              <BoxElement
                content={formatNumberValue(value?.toProgress, "")}
                error={toProgressNumberError}
              />
            </>
          ) : (
            <BoxElement
              content={formatNumberValue(value?.progress, "")}
              error={progressNumberError}
            />
          )}
        </>
      ) : (
        <>
          <SelectInput
            displaySearch
            items={achievementsItems}
            label={
              <RequiredFieldLabel
                text={message({
                  id: "achievementName",
                })}
              />
            }
            name={`${formField}.achievementId`}
            data-test-id="achievement-progression-name"
            className={classes.input}
            asyncSearch={(searchedQuery: string) =>
              setAchievementSearch(searchedQuery)
            }
            initialValue={{
              id: selectedAchievementData?.achievementId || "",
              label: selectedAchievementName || "",
            }}
            onChange={(_, value) => {
              setFieldValue(`${formField}.achievementId`, value);
              setFieldValue(`${formField}.achievementRuleId`, "");
            }}
            loadMoreElements={() =>
              !isValidating && setAchievementsSize(achievementsSize + 1)
            }
            anyElementsToFetch={anyAchievementsToFetch}
          />
          {value?.achievementId && (
            <>
              <SelectInput
                disabled={isSelectDisabled}
                items={selectedAchievementRulesItems}
                label={
                  <RequiredFieldLabel
                    text={message({
                      id: "achievementRule",
                    })}
                  />
                }
                name={`${formField}.achievementRuleId`}
                data-test-id="achievement-progression-ruleId"
                className={classes.input}
                onChange={(e) => {
                  setFieldValue(
                    `${formField}.achievementRuleId`,
                    e.target.value,
                  );

                  if (!checkIfConsecutiveRule(e.target.value)) {
                    setFieldValue(`${formField}.achievementGoal`, undefined);
                  }
                }}
              />
              {checkIfConsecutiveRule(selectedRule?.id) && (
                <SelectInput
                  items={segmentAchievementGoal.map(
                    ({ id, label }: { id: string; label: string }) => ({
                      id: id,
                      label: message({ id: label }),
                    }),
                  )}
                  label={
                    <RequiredFieldLabel
                      text={message({
                        id: "achievementGoal",
                      })}
                    />
                  }
                  name={`${formField}.achievementGoal`}
                  data-test-id="achievement-progression-goal"
                  className={classes.input}
                />
              )}
              <SelectInput
                className={
                  !isProgressCountRange ||
                  checkIfConsecutiveRule(selectedRule?.id)
                    ? classes.input
                    : ""
                }
                data-test-id="achievement-progression-operator"
                items={segmentConditions.map(
                  ({ id, label }: { id: string; label: string }) => ({
                    id: id,
                    label: message({ id: label }),
                  }),
                )}
                label={
                  <RequiredFieldLabel text={message({ id: "condition" })} />
                }
                name={`${formField}.operator.code`}
                onChange={(_, value) => {
                  if (value === "is_number_between") {
                    setFieldValue(`${formField}.progress`, undefined);
                    setFieldValue(`${formField}.fromProgress`, 0);
                    setFieldValue(`${formField}.toProgress`, 0);
                  } else {
                    setFieldValue(`${formField}.progress`, 0);
                    setFieldValue(`${formField}.fromProgress`, undefined);
                    setFieldValue(`${formField}.toProgress`, undefined);
                  }
                }}
              />
              {isProgressCountRange && (
                <>
                  <NumberFormatInput
                    allowDecimals
                    allowNegative={false}
                    className={classes.input}
                    data-test-id="progression-number-from"
                    label={
                      <RequiredFieldLabel
                        text={message({
                          id: isCompletedPeriodsAchievementGoal
                            ? "completedPeriodsFrom"
                            : "currentProgressFrom",
                        })}
                      />
                    }
                    name={`${formField}.fromProgress`}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          {selectedRuleAttrLabel}
                        </InputAdornment>
                      ),
                    }}
                  />
                  <NumberFormatInput
                    allowDecimals
                    allowNegative={false}
                    className={classes.input}
                    data-test-id="progression-number-to"
                    label={
                      <RequiredFieldLabel
                        text={message({
                          id: isCompletedPeriodsAchievementGoal
                            ? "completedPeriodsTo"
                            : "currentProgressTo",
                        })}
                      />
                    }
                    name={`${formField}.toProgress`}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          {selectedRuleAttrLabel}
                        </InputAdornment>
                      ),
                    }}
                  />
                </>
              )}
              {!isProgressCountRange && !!value.operator.code && (
                <NumberFormatInput
                  allowDecimals
                  allowNegative={false}
                  data-test-id="progress-number"
                  label={
                    <RequiredFieldLabel
                      text={message({
                        id: isCompletedPeriodsAchievementGoal
                          ? "completedPeriods"
                          : "currentProgress",
                      })}
                    />
                  }
                  name={`${formField}.progress`}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {selectedRuleAttrLabel}
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            </>
          )}
        </>
      )}
    </>
  );
}
